import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Rocket, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_BORDERS = {
  free: 'border-white/10',
  premium: 'border-[#F59E0B]/40',
  ultimate: 'border-[#E11D48]/50',
};
const TIER_NAMES = {
  free: 'text-white',
  premium: 'text-[#F59E0B]',
  ultimate: 'text-[#E11D48]',
};

function formatRemaining(boostedUntil) {
  const ms = new Date(boostedUntil).getTime() - Date.now();
  if (ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  const s = Math.max(1, Math.floor(ms / 1000));
  return `${s}s`;
}

export default function BoostedRail() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tick state forces re-render every 30s so countdown stays live
  const [, setTick] = useState(0);
  const fetchedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/boosted-characters?limit=12`);
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    fetchedRef.current = true;
    // Refresh every 2 min in case new boosts appear
    const fetchInterval = setInterval(load, 120000);
    // Tick every 30s for live countdown
    const tickInterval = setInterval(() => setTick(t => t + 1), 30000);
    return () => { cancelled = true; clearInterval(fetchInterval); clearInterval(tickInterval); };
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section className="mb-6" data-testid="boosted-rail">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-full bg-purple-500/15 flex items-center justify-center">
          <Rocket className="w-3.5 h-3.5 text-purple-400" />
        </div>
        <h2 className="text-sm font-bold text-white">Boosted Right Now</h2>
        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 rounded-full">LIVE</span>
      </div>

      <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide">
        <div className="flex gap-3 pb-1">
          {loading
            ? [...Array(4)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-36 h-44 rounded-2xl bg-[#111318] border border-white/5 animate-pulse" />
              ))
            : items.map((c) => {
                const remaining = formatRemaining(c.boosted_until);
                if (!remaining) return null;
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/character/${c.id}`)}
                    className={`flex-shrink-0 w-36 relative rounded-2xl overflow-hidden border ${TIER_BORDERS[c.creator_tier] || 'border-white/10'} bg-[#111318] active:scale-95 transition-transform`}
                    style={{ scrollSnapAlign: 'start' }}
                    data-testid={`boosted-card-${c.id}`}
                  >
                    <div className="relative aspect-[3/4] w-full">
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1D24] to-[#0F1115]" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                      {/* Live countdown */}
                      <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/60 backdrop-blur rounded-full">
                        <Clock className="w-2.5 h-2.5 text-purple-300" />
                        <span className="text-[9px] font-bold text-purple-200 tabular-nums">{remaining}</span>
                      </div>
                      {/* Boosted badge */}
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-purple-500/30 backdrop-blur border border-purple-500/40 rounded-full">
                        <Rocket className="w-2.5 h-2.5 text-purple-200" />
                      </div>
                      {/* Name overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <p className={`text-xs font-bold ${TIER_NAMES[c.creator_tier] || 'text-white'} line-clamp-1`} style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>{c.name}</p>
                        <p className="text-[10px] text-white/60 line-clamp-1 mt-0.5">by {c.creator_name}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
        </div>
      </div>
    </section>
  );
}
